import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form } from '@unform/web';

import {
  AddIcon,
  CardPlusIcon,
  ChevronDownIcon,
  UploadIcon,
} from '../../../assets/svg/SVGComponents';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import FileUploadInput from '../../../components/FileUploadInput';
import Input from '../../../components/Input';
import CreatableSelect from '../../../components/Selects/CreatableSelect';
import Select from '../../../components/Selects/SimpleSelect';
import { IPostMedia, IPostProjectProps } from '../../../dtos/IPostProjectProps';
import { useAuth } from '../../../hooks/auth';
import { useImageModeration } from '../../../hooks/useImageModeration';
import api from '../../../services/api';
import { gradeTexts, hardwareTexts } from '../../../utils/enums';
import {
  ActionsRow,
  Basicnfo,
  CancelButton,
  Container,
  CurrentMediaGrid,
  CurrentMediaPanel,
  ErrorHint,
  HeaderContainer,
  InfoPost,
  InputInfoPost,
  LeftSideContent,
  LockedFieldsHint,
  MediaBadge,
  MediaThumb,
  MediaToggle,
  RightSideContent,
  SubmitHint,
  UserAvatar,
  subjectsSelectStyles,
  toolSelectStyles,
} from './styles';

/** Espelha MAX_POST_MEDIA do backend. */
const MAX_MEDIA = 5;

const DEFAULT_AVATAR =
  'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png';

interface IEditFormData {
  title: string;
  description: string;
  tags?: string[];
}

/** Rótulo legível de um valor de enum, caindo no valor cru se não houver. */
function labelFor(dictionary: Record<string, string>, value: string): string {
  return dictionary[value] ?? value;
}

/**
 * Edição de um projeto já publicado.
 *
 * Segue o mesmo desenho da publicação de propósito: mesmas colunas, mesmo
 * cartão de informações básicas, mesmos campos na mesma ordem. Quem já
 * publicou uma vez não precisa reaprender a tela.
 *
 * Escolaridade e kit aparecem como os mesmos selects, porém desabilitados.
 * Some-los seria mais simples e pior: o aluno acharia que a informação se
 * perdeu. Mudá-los depois reclassificaria um post que já pontuou.
 *
 * Mídia nova passa pela mesma moderação da publicação, e é isso que impede
 * usar a edição para trocar o conteúdo depois de o post juntar curtidas.
 */
const EditPost: React.FC = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [post, setPost] = useState<IPostProjectProps>();
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  /** Ids de post_photos marcados para sair; só viram remoção ao salvar. */
  const [removedMediaIds, setRemovedMediaIds] = useState<string[]>([]);

  const {
    entries: moderationEntries,
    analyzeFile,
    removeEntry: removeModerationEntry,
    hasPending,
    hasRejected,
    hasUnsupported,
    hasMediaAwaitingReview,
    approvedKeys,
  } = useImageModeration();

  useEffect(() => {
    if (!postId) return;

    api
      .get<IPostProjectProps>(`/posts/${postId}`)
      .then(response => setPost(response.data))
      .catch(() => setLoadError('Não foi possível carregar este projeto.'));
  }, [postId]);

  const handleFileAdded = useCallback(
    (fileId: string, file: File) => analyzeFile(fileId, file),
    [analyzeFile],
  );

  const handleFileRemoved = useCallback(
    (fileId: string) => removeModerationEntry(fileId),
    [removeModerationEntry],
  );

  const toggleMediaRemoval = useCallback((media_id: string) => {
    setRemovedMediaIds(previous =>
      previous.includes(media_id)
        ? previous.filter(id => id !== media_id)
        : [...previous, media_id],
    );
  }, []);

  const currentMedia = useMemo(() => post?.photos ?? [], [post]);

  const finalMediaCount =
    currentMedia.length - removedMediaIds.length + approvedKeys.length;

  const isOwner = !!post && post.user.user_id === user.id;

  const initialData = useMemo(() => {
    if (!post) return undefined;

    return {
      title: post.title,
      description: post.description,
      // react-select trabalha com {label, value}; o unform devolve só o value.
      tags: post.tags.map(tag => ({ label: tag, value: tag })),
      grade: {
        label: labelFor(gradeTexts, post.grade),
        value: post.grade,
      },
      hardware: {
        label: labelFor(hardwareTexts, post.hardware),
        value: post.hardware,
      },
    };
  }, [post]);

  const handleSubmit = useCallback(
    async (data: IEditFormData) => {
      if (!post) return;

      setSubmitError('');
      setIsSaving(true);

      try {
        // grade e hardware não vão no corpo — o backend recusa os dois.
        await api.patch(`/posts/${post.id}`, {
          title: data.title?.trim(),
          description: data.description?.trim(),
          tags: data.tags ?? [],
          // Só o que passou pela moderação — mesma regra da publicação.
          ...(approvedKeys.length > 0 && { upload_keys: approvedKeys }),
          ...(removedMediaIds.length > 0 && {
            remove_photo_ids: removedMediaIds,
          }),
        });

        navigate(`/community/post/${post.id}`);
      } catch {
        setSubmitError('Não foi possível salvar as alterações. Tente de novo.');
      } finally {
        setIsSaving(false);
      }
    },
    [post, approvedKeys, removedMediaIds, navigate],
  );

  const formatCreateLabel = useCallback((inputValue: string) => {
    return (
      <p>
        <b>Criar nova:</b> {inputValue}
      </p>
    );
  }, []);

  const submitBlocked =
    hasPending ||
    hasRejected ||
    finalMediaCount < 1 ||
    finalMediaCount > MAX_MEDIA;

  const submitLabel = useMemo(() => {
    if (hasPending) return 'Verificando arquivos…';
    if (hasRejected) return 'Remova os arquivos rejeitados';
    if (finalMediaCount < 1) return 'O projeto precisa de ao menos um arquivo';
    if (finalMediaCount > MAX_MEDIA)
      return `No máximo ${MAX_MEDIA} arquivos (ficaria com ${finalMediaCount})`;
    return 'Salvar alterações';
  }, [hasPending, hasRejected, finalMediaCount]);

  if (loadError) {
    return (
      <Container>
        <LeftSideContent>
          <ErrorHint>{loadError}</ErrorHint>
        </LeftSideContent>
      </Container>
    );
  }

  if (!post) return null;

  if (!isOwner) {
    return (
      <Container>
        <LeftSideContent>
          <ErrorHint>Só o autor pode editar este projeto.</ErrorHint>
        </LeftSideContent>
      </Container>
    );
  }

  return (
    <Container>
      <LeftSideContent>
        <HeaderContainer>
          <CardPlusIcon
            width="2rem"
            stroke="#FF9800"
            strokeWidth={3}
            fill="none"
          />
          <h1>Editar seu projeto</h1>
        </HeaderContainer>

        <InputInfoPost>
          <Form initialData={initialData} onSubmit={handleSubmit}>
            <Basicnfo>
              <header>
                <UserAvatar avatarSrc={user.avatar_url || DEFAULT_AVATAR} />
                <div>
                  <h2>{user.name}</h2>
                  <span>{user.school?.name}</span>
                </div>
              </header>

              <section>
                <div>
                  <h4>Nível de escolaridade</h4>
                  <Select
                    name="grade"
                    dropdownIcon={ChevronDownIcon}
                    styles={toolSelectStyles}
                    isDisabled
                  />
                </div>
                <div>
                  <h4>Ferramenta utilizada</h4>
                  <Select
                    name="hardware"
                    dropdownIcon={ChevronDownIcon}
                    styles={toolSelectStyles}
                    isDisabled
                  />
                </div>
              </section>

              <LockedFieldsHint>
                Escolaridade e kit não mudam depois de publicado.
              </LockedFieldsHint>

              <div>
                <h4>Palavras chaves </h4>
                <CreatableSelect
                  name="tags"
                  isMulti
                  placeholder="Palavras chaves"
                  dropdownIcon={AddIcon}
                  styles={subjectsSelectStyles}
                  isClearable
                  formatCreateLabel={formatCreateLabel}
                />
              </div>
            </Basicnfo>

            <InfoPost>
              <div>
                <h4>Título*</h4>
                <Input name="title" placeholder="Título do projeto" />
              </div>
              <div>
                <h4>Descrição*</h4>
                <Input
                  name="description"
                  placeholder="Resumo breve do projeto"
                  multiline
                />
              </div>

              <div>
                <h4>Adicionar arquivos</h4>
                <FileUploadInput
                  name="attachments"
                  onFileAdded={handleFileAdded}
                  onFileRemoved={handleFileRemoved}
                  moderationEntries={moderationEntries}
                />
              </div>
            </InfoPost>

            {hasUnsupported && (
              <SubmitHint>
                Os arquivos marcados como “Não enviado” ficam de fora. O resto
                das alterações pode ser salvo normalmente.
              </SubmitHint>
            )}

            {hasMediaAwaitingReview && (
              <SubmitHint>
                Os arquivos que você acabou de adicionar passam por avaliação da
                nossa equipe antes de aparecer para todo mundo. O resto das
                alterações entra no ar assim que você salvar.
              </SubmitHint>
            )}

            {submitError && <ErrorHint>{submitError}</ErrorHint>}

            <ActionsRow>
              <PrimaryButton
                type="submit"
                loading={isSaving}
                disabled={submitBlocked}
              >
                <UploadIcon width="1.4rem" stroke="#fff" strokeWidth={3} />
                {submitLabel}
              </PrimaryButton>
              <CancelButton
                type="button"
                onClick={() => navigate(`/community/post/${post.id}`)}
              >
                Cancelar
              </CancelButton>
            </ActionsRow>
          </Form>
        </InputInfoPost>
      </LeftSideContent>

      <RightSideContent>
        <CurrentMediaPanel>
          <h1>
            <CardPlusIcon
              width="2rem"
              stroke="#FF9800"
              strokeWidth={3}
              fill="none"
            />
            Arquivos atuais
          </h1>
          <p>
            {currentMedia.length} de {MAX_MEDIA}. Use o × para marcar um arquivo
            para remoção — ele só sai de fato quando você salvar.
          </p>

          <CurrentMediaGrid>
            {currentMedia.map((media: IPostMedia) => {
              const isRemoved = removedMediaIds.includes(media.id);

              return (
                <MediaThumb key={media.id} $removed={isRemoved}>
                  {media.media_type === 'video' ? (
                    // Sem autoplay e sem controles: a miniatura só precisa
                    // dizer qual vídeo é. O #t=0.1 é o que faz o navegador
                    // desenhar um quadro em vez de um retângulo preto.
                    <video
                      src={`${media.url}#t=0.1`}
                      preload="metadata"
                      muted
                    />
                  ) : (
                    <img src={media.url} alt="Anexo do projeto" />
                  )}

                  {media.moderation_status === 'pending_review' && (
                    <MediaBadge>Em avaliação</MediaBadge>
                  )}

                  <MediaToggle
                    type="button"
                    $removed={isRemoved}
                    onClick={() => toggleMediaRemoval(media.id)}
                    aria-label={
                      isRemoved ? 'Manter este arquivo' : 'Remover este arquivo'
                    }
                  >
                    {isRemoved ? '↺' : '×'}
                  </MediaToggle>
                </MediaThumb>
              );
            })}
          </CurrentMediaGrid>
        </CurrentMediaPanel>
      </RightSideContent>
    </Container>
  );
};

export default EditPost;
